import { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { API_CONFIG } from '../config/api'

const ADMIN_HOST_SUFFIX = 'agencycms.com'

// Builds https://<subdomain>.agencycms.com/admin, keeping any deeper admin path,
// query string and hash (e.g. /admin/packages?x=1 -> <sub>.agencycms.com/admin/packages?x=1)
export const buildAdminUrl = (subdomain: string, pathname = '/admin', search = '', hash = '') => {
  const subPath = pathname.replace(/^\/admin/i, '')
  return `https://${subdomain}.${ADMIN_HOST_SUFFIX}/admin${subPath}${search}${hash}`
}

// /admin on the custom site sends the agent to their Agency CMS admin.
// The subdomain comes from the franchise settings in the CMS, so every
// branch/site redirects to its own admin without any per-site config.
export default function AdminRedirect() {
  const { pathname, search, hash } = useLocation()
  const [failed, setFailed] = useState(false)
  const [attempt, setAttempt] = useState(0)

  useEffect(() => {
    let cancelled = false
    setFailed(false)

    const redirect = async () => {
      try {
        // Fetched directly (not via FranchiseService) so a failed request never
        // falls back to the demo franchise and sends the agent to the wrong admin
        const response = await fetch(
          `${API_CONFIG.BASE_URL}/franchises/${API_CONFIG.FRANCHISE_ID}`,
          {
            headers: {
              'x-api-key': API_CONFIG.API_KEY,
              'Content-Type': 'application/json',
            },
          }
        )
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }
        const data: { subdomain?: string | null } = await response.json()
        const subdomain = data.subdomain?.trim().toLowerCase()
        if (!subdomain || !/^[a-z0-9-]+$/.test(subdomain)) {
          throw new Error('Franchise has no valid subdomain configured')
        }
        if (!cancelled) {
          window.location.replace(buildAdminUrl(subdomain, pathname, search, hash))
        }
      } catch (error) {
        console.error('Error redirecting to admin:', error)
        if (!cancelled) setFailed(true)
      }
    }

    redirect()
    return () => {
      cancelled = true
    }
  }, [pathname, search, hash, attempt])

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '1rem',
        padding: '1.5rem',
        background: '#ffffff',
        color: '#1f2937',
        fontFamily: 'system-ui, -apple-system, sans-serif',
        textAlign: 'center',
      }}
    >
      {failed ? (
        <>
          <p style={{ fontSize: '1.125rem' }}>We couldn't open the admin right now.</p>
          <button
            type="button"
            onClick={() => setAttempt((n) => n + 1)}
            style={{
              padding: '0.5rem 1.25rem',
              borderRadius: '0.5rem',
              border: '1px solid #d1d5db',
              background: '#f9fafb',
              cursor: 'pointer',
            }}
          >
            Try again
          </button>
        </>
      ) : (
        <p style={{ fontSize: '1.125rem' }}>Redirecting to admin…</p>
      )}
    </div>
  )
}
